import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPlay } from '@fortawesome/free-solid-svg-icons';
import './media-preview-modal.scss';

// Module-level cache - persists across component mounts
const mediaCache = new Map();

const MediaPreviewModal = ({ show, onHide, contentID, filename }) => {
    const [mediaData, setMediaData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const videoRef = useRef(null);

    useEffect(() => {
        if (show && contentID) {
            fetchMedia();
        }

        return () => {
            // Pause video when modal closes
            if (videoRef.current) {
                videoRef.current.pause();
            }
        };
    }, [show, contentID]);

    const fetchMedia = async () => {
        // Check cache first
        if (mediaCache.has(contentID)) {
            setMediaData(mediaCache.get(contentID));
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/api/DigitalSignage/${contentID}/content`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Failed to load media');
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            const data = {
                url: url,
                type: blob.type
            };

            // Store in cache
            mediaCache.set(contentID, data);
            setMediaData(data);

        } catch (err) {
            console.error('Error fetching media:', err);
            setError('Failed to load media');
        } finally {
            setLoading(false);
        }
    };

    const isVideo = mediaData?.type?.startsWith('video/');

    return (
        <Modal
            centered
            onHide={onHide}
            show={show}
            size="lg"
        >
            <Modal.Header closeButton>
                <Modal.Title
className="text-truncate"
style={{ maxWidth: '90%' }}
                >
                    {filename || 'Preview'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center p-0">
                {loading && (
                    <div className="py-5">
                        <FontAwesomeIcon
icon={faSpinner}
size="3x"
spin
                        />
                        <p className="mt-3">Loading preview...</p>
                    </div>
                )}

                {error && (
                    <div className="py-5 text-danger">
                        <p>{error}</p>
                    </div>
                )}

                {!loading && !error && mediaData && (
                    <div className="media-container">
                        {isVideo ? (
                            <video
                                controls
                                ref={videoRef}
                                src={mediaData.url}
                                style={{ maxWidth: '100%', maxHeight: '70vh' }}
                            />
                        ) : (
                            <img
                                alt={filename || 'Content preview'}
                                src={mediaData.url}
                                style={{ maxWidth: '100%', maxHeight: '70vh' }}
                            />
                        )}
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

// Export cache clear function for cleanup if needed
export const clearMediaCache = () => {
    mediaCache.forEach((data) => {
        if (data.url) {
            URL.revokeObjectURL(data.url);
        }
    });
    mediaCache.clear();
};

export default MediaPreviewModal;